export type GpsTriggerSpatialEnvelope = {
    sourceIndex: number;
    south: number;
    west: number;
    north: number;
    east: number;
};

const defaultCellSizeDegrees = 0.005;

export class GpsTriggerSpatialIndex {
    private readonly cells = new Map<string, number[]>();

    constructor(
        envelopes: readonly GpsTriggerSpatialEnvelope[],
        private readonly cellSizeDegrees = defaultCellSizeDegrees
    ) {
        for (const envelope of envelopes) {
            this.add(envelope);
        }
    }

    queryPoint(latitude: number, longitude: number): readonly number[] {
        return this.cells.get(this.key(this.cell(latitude), this.cell(longitude))) ?? [];
    }

    queryEnvelope(south: number, west: number, north: number, east: number): readonly number[] {
        const candidates = new Set<number>();
        this.forEachCell(south, west, north, east, key => {
            for (const sourceIndex of this.cells.get(key) ?? []) {
                candidates.add(sourceIndex);
            }
        });
        return Array.from(candidates).sort((left, right) => left - right);
    }

    private add(envelope: GpsTriggerSpatialEnvelope): void {
        this.forEachCell(envelope.south, envelope.west, envelope.north, envelope.east, key => {
            const candidates = this.cells.get(key);
            if (!candidates) {
                this.cells.set(key, [envelope.sourceIndex]);
            } else if (candidates.at(-1) !== envelope.sourceIndex) {
                candidates.push(envelope.sourceIndex);
            }
        });
    }

    private forEachCell(
        south: number,
        west: number,
        north: number,
        east: number,
        action: (key: string) => void
    ): void {
        const southCell = this.cell(Math.min(south, north));
        const northCell = this.cell(Math.max(south, north));
        const westCell = this.cell(Math.min(west, east));
        const eastCell = this.cell(Math.max(west, east));
        for (let latitudeCell = southCell; latitudeCell <= northCell; latitudeCell++) {
            for (let longitudeCell = westCell; longitudeCell <= eastCell; longitudeCell++) {
                action(this.key(latitudeCell, longitudeCell));
            }
        }
    }

    private cell(value: number): number {
        return Math.floor(value / this.cellSizeDegrees);
    }

    private key(latitudeCell: number, longitudeCell: number): string {
        return `${latitudeCell}:${longitudeCell}`;
    }
}
